import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import {
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { eventTeamApiService } from '../../../services/eventTeamApi';
import type { EventItem } from '../../../types/eventTeam';
import { getEventStatusColor } from '../EventStatusBadge';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { cn } from '../../../utils/cn';
import { SectionHeader } from '../../ui/SectionHeader';
import { SkeletonLoader } from '../../SkeletonLoader';

export interface EventCalendarPanelProps {
  onOpenEvent: (id: string) => void;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Month-view calendar tab (Roll's request: "a tab with a calendar would be nice to
 * make things easier to see in one place" — month view). Shows every event with a
 * run window in the visible grid, colored by pipeline status; click → detail.
 */
export const EventCalendarPanel = ({ onOpenEvent }: EventCalendarPanelProps) => {
  const isMobile = useIsMobile();

  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const gridStart = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Bound the fetch to the visible grid so months stay light.
      const res = await eventTeamApiService.listEvents({
        scheduled_after: gridStart.getTime(),
        scheduled_before: gridEnd.getTime(),
        sort_by: 'scheduled_start',
        sort_order: 'asc',
        limit: 200,
      });
      setEvents(res.results.filter((e) => e.scheduled_start != null));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the calendar');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate.getFullYear(), currentDate.getMonth()]);

  useEffect(() => {
    void load();
  }, [load]);

  const days = useMemo(() => eachDayOfInterval({ start: gridStart, end: gridEnd }), [gridStart.getTime(), gridEnd.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Events per day — run windows spanning multiple days appear on each day. */
  const eventsByDay = useMemo(() => {
    const map = new Map<string, EventItem[]>();
    for (const event of events) {
      if (event.scheduled_start == null) continue;
      const start = startOfDay(new Date(event.scheduled_start));
      const end = endOfDay(new Date(event.scheduled_end ?? event.scheduled_start));
      const clampedStart = start < gridStart ? gridStart : start;
      const clampedEnd = end > gridEnd ? gridEnd : end;
      if (clampedStart > clampedEnd) continue;
      for (const day of eachDayOfInterval({ start: clampedStart, end: clampedEnd })) {
        const key = format(day, 'yyyy-MM-dd');
        const list = map.get(key) ?? [];
        if (!list.some((e) => e.id === event.id)) list.push(event);
        map.set(key, list);
      }
    }
    return map;
  }, [events, gridStart.getTime(), gridEnd.getTime()]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxChips = isMobile ? 2 : 3;

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Event Calendar"
        icon={CalendarDays}
        iconClassName="text-cyan-400"
        description="Every event with a run window, coloured by pipeline status."
        className="mb-0 flex-wrap"
        actions={
          <>
            <button
              type="button"
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              className="btn-icon"
              title="Previous month"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setCurrentDate(new Date())} className="glass-button-secondary btn-xs">
              Today
            </button>
            <button
              type="button"
              onClick={() => setCurrentDate(addMonths(currentDate, 1))}
              className="btn-icon"
              title="Next month"
              aria-label="Next month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-sm font-semibold text-white ml-1 min-w-[120px] text-right tabular-nums">
              {format(currentDate, 'MMMM yyyy')}
            </span>
          </>
        }
      />

      {error && (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      )}

      {loading ? (
        <div className="glass-card p-3 space-y-2" aria-busy="true">
          <SkeletonLoader className="h-6 w-full" />
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonLoader key={i} className={isMobile ? 'h-16 w-full' : 'h-24 w-full'} />
          ))}
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          {/* Weekday header */}
          <div className="grid grid-cols-7 border-b border-glass-200/10">
            {WEEKDAYS.map((d) => (
              <div key={d} className="eyebrow px-2 py-2 text-center">
                {isMobile ? d.slice(0, 1) : d}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const dayEvents = eventsByDay.get(key) ?? [];
              const inMonth = isSameMonth(day, currentDate);
              const overflow = dayEvents.length - maxChips;
              return (
                <div
                  key={key}
                  className={cn(
                    'border-b border-r border-glass-200/5 p-1 align-top',
                    isMobile ? 'min-h-[64px]' : 'min-h-[104px]',
                    !inMonth && 'bg-dark-900/30 opacity-50',
                  )}
                >
                  <div
                    className={cn(
                      'text-[11px] mb-1 w-5 h-5 flex items-center justify-center rounded-full',
                      isToday(day) ? 'bg-accent-500 text-on-solid font-semibold' : 'text-glass-400',
                    )}
                  >
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, maxChips).map((event) => {
                      const color = getEventStatusColor(event.status, 'event');
                      const startsToday = event.scheduled_start != null && isSameDay(new Date(event.scheduled_start), day);
                      return (
                        <button
                          key={event.id}
                          onClick={() => onOpenEvent(event.id)}
                          className={cn(
                            'w-full text-left rounded px-1 py-0.5 text-[10px] leading-tight truncate border hover:brightness-125 transition-all',
                            color.bg,
                            color.text,
                            color.border,
                          )}
                          title={`${event.title} (${color.label})${
                            event.scheduled_start ? ` · ${format(new Date(event.scheduled_start), 'MMM d HH:mm')}` : ''
                          }`}
                        >
                          {!isMobile && startsToday && event.scheduled_start != null && (
                            <span className="font-mono mr-1 opacity-80">{format(new Date(event.scheduled_start), 'HH:mm')}</span>
                          )}
                          {event.title}
                        </button>
                      );
                    })}
                    {overflow > 0 && (
                      <p className="text-[9px] text-glass-500 px-1">+{overflow} more</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Status legend */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['idea', 'approved', 'scheduled', 'announced', 'in_progress', 'completed', 'on_hold', 'cancelled'] as const).map(
          (status) => {
            const color = getEventStatusColor(status, 'event');
            return (
              <span
                key={status}
                className={cn('badge badge-dot', color.bg, color.text, color.border)}
              >
                {color.label}
              </span>
            );
          },
        )}
      </div>
    </div>
  );
};
